import MarkdownIt from 'markdown-it'

// `html: false` is the XSS guard for model-generated input: never turn it on.
const md = new MarkdownIt({ html: false, linkify: true, breaks: true })

export function renderMarkdown(src: string): string {
  return md.render(src ?? '')
}

/** One `## ` part of a document body: the heading text, and the DOM id it renders with. */
export interface DocumentHeading {
  readonly id: string
  readonly label: string
}

const HEADING = /^##\s+(.+?)\s*$/
const FENCE = /^\s*(```|~~~)/

function slugify(title: string, fallback: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug === '' ? fallback : slug
}

/** The `## ` headings of a document body, in document order; only H2 counts and fenced code is skipped. */
export function documentHeadings(src: string): DocumentHeading[] {
  const headings: DocumentHeading[] = []
  const used = new Set<string>()
  let fenced = false

  for (const line of (src ?? '').split(/\r?\n/)) {
    if (FENCE.test(line)) fenced = !fenced
    if (fenced) continue

    const label = HEADING.exec(line)?.[1]
    if (label === undefined) continue

    const base = slugify(label, `part-${headings.length + 1}`)
    let id = `doc-${base}`
    for (let suffix = 2; used.has(id); suffix += 1) id = `doc-${base}-${suffix}`
    used.add(id)
    headings.push({ id, label })
  }

  return headings
}

const documentMd = new MarkdownIt({ html: false, linkify: true, breaks: true })

// Ids come from `documentHeadings` so the outline and the document agree; both walk H2s in the same order.
documentMd.renderer.rules.heading_open = (tokens, index, options, env, self) => {
  const token = tokens[index]
  const ids = (env as { headingIds?: string[] }).headingIds
  if (token?.tag === 'h2' && ids) {
    const id = ids.shift()
    if (id) token.attrSet('id', id)
  }
  return self.renderToken(tokens, index, options)
}

/** Renders a document body, giving every H2 the id its outline entry links to. */
export function renderDocument(src: string): string {
  return documentMd.render(src ?? '', {
    headingIds: documentHeadings(src).map((heading) => heading.id),
  })
}

const INLINE_TEXT_TOKENS: ReadonlySet<string> = new Set(['text', 'code_inline'])
const INLINE_BREAK_TOKENS: ReadonlySet<string> = new Set(['softbreak', 'hardbreak'])

/** The text a reader sees for one line of inline markdown: link targets, emphasis marks and code ticks are dropped. */
export function markdownText(src: string): string {
  return documentMd
    .parseInline(src, {})
    .flatMap((token) => token.children ?? [])
    .map((child) => {
      if (INLINE_TEXT_TOKENS.has(child.type)) return child.content
      return INLINE_BREAK_TOKENS.has(child.type) ? ' ' : ''
    })
    .join('')
}
